// SecurityPanel — My Account -> "Profile & security" section -> "Sign-in &
// security" tab (Task 10 of the 2026-08-30 marketplace-customer-passkeys
// plan; originally an ungated sub-section of Profile — ⚠️ AMENDED 2026-10-04,
// account-area redesign Task 8: it is now that section's tab content,
// mounted ONLY while the tab is active, see ProfilePanel.tsx). Lists the
// signed-in
// customer's own passkeys with rename/add/delete. Anti-enumeration: this
// component only ever calls listPasskeys()/renamePasskey()/deletePasskey()
// against the caller's own Bearer session — nothing here can name, count, or
// reveal anything about another customer's credentials.
//
// Operator ruling R1 (non-negotiable, see task-10 dispatch brief): deleting a
// passkey here removes ONLY our server record of it. The customer's device/
// authenticator may still list the credential until the customer removes it
// themselves in their OWN device settings. No copy anywhere below may state
// or imply we removed it FROM THE DEVICE -- every string says what's true:
// it stops working for sign-in here, and clearing the device-side entry is
// the customer's own action to take.
//
// Operator ruling R2: email OTP remains the account's unaffected, non-
// degraded sign-in path. Passkeys are presented as an additional
// convenience, never as a replacement being phased in.
//
// Consumes Task 8's client VERBATIM (embed/marketplace/api/passkeys.ts,
// embed-shared/passkey.ts) -- this file does not modify either; both are
// being fixed concurrently in a sibling worktree (internal-only changes,
// signatures unchanged). Cancellation detection uses the STABLE contract
// (`err.name === "NotAllowedError"`), never `instanceof <class>` -- the
// cancellation error class is mid-rename there. Same pattern SignInPanel.tsx
// already uses for signInWithPasskey().
//
// Inline styles only -- no Tailwind (embed convention; matches every sibling
// panel: MidsPanel/ReferPanel/AccountCenter). Every <button> sets
// type="button" at birth (rule 73) so a future wrap-in-<form> refactor can't
// silently turn one into a submit button; the one true type="submit" is the
// Add-passkey form's own submit action.
//
// Rule 59: rename re-seeds the passkey row from the MUTATION RESPONSE (the
// server-normalized friendly_name), never the locally-typed value.
// Rule 8: registerPasskey/renamePasskey/deletePasskey calls all use
// async/await -- no nested .mutate()/.then() chains.

import { useState, useCallback, useEffect, useRef, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedHeading,
  EmbedText,
  EmbedButton,
  EmbedLink,
  EmbedBanner,
  EmbedField,
  EmbedInput,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { isPasskeySupported } from "../../embed-shared/passkey";
import { formatRelativeOrAbsoluteDate } from "./date-display";
import {
  deletePasskey,
  listPasskeys,
  registerPasskey,
  renamePasskey,
  type PasskeySummary,
} from "./api/passkeys";
import { ceremonyMessageOf } from "./passkey-ceremony-errors";

/**
 * Display label for a passkey row. The Task 8 interface types
 * `friendly_name` as a non-null `string`, but the underlying DB column is
 * nullable and the operator-side DTO types the same concept `string | null`
 * (see portal/api/passkey.ts) -- so this guards with `typeof` at runtime
 * rather than trusting the compile-time type, and never calls
 * `.trim()`/`.length` on a value that might actually be null.
 */
function displayName(pk: PasskeySummary): string {
  const raw: unknown = pk.friendly_name;
  if (typeof raw !== "string") return "Unnamed passkey";
  const trimmed = raw.trim();
  return trimmed === "" ? "Unnamed passkey" : trimmed;
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// ceremonyMessageOf (register/sign-in ceremony failures) now lives in
// ./passkey-ceremony-errors -- fix-round-2 I-3 extracted it there so
// SignInPanel.tsx and PasskeyEnrollPrompt.tsx (the two other ceremony call
// sites that used to collapse every non-cancellation failure into generic
// "Please try again" copy) can share the SAME allow-list and fallback
// behavior this file originated. See that module's header for the full
// rationale (a SecurityError from an RP-ID mismatch is PERMANENT, and "try
// again" invites the one action guaranteed not to work) and Operator ruling
// R2 (email OTP is never presented as replaced or degraded).

/**
 * True for the ordinary "user dismissed the authenticator prompt" outcome.
 * Branch on the STABLE name, never the class -- see file header.
 */
function isCancellation(err: unknown): boolean {
  return err instanceof Error && err.name === "NotAllowedError";
}

export interface SecurityPanelProps {
  /**
   * Fix-round-2 I-1: whether THIS MOUNT is allowed to offer passkey
   * REGISTRATION, resolved at RUNTIME exactly like SignInPanel's
   * identically-named prop (see that file's doc comment for the full
   * rationale -- the reCAPTCHA build-time-key incident this pattern exists
   * to avoid repeating). Strict `=== true`, not bare truthiness -- same
   * reasoning as SignInPanel's showPasskeySignIn: a malformed config value
   * must not enable the surface. Absent/false is the safe default.
   *
   * Gates ONLY the "Add a Passkey" registration ceremony below -- NOT the
   * existing-passkey list/rename/delete above. Rename/Delete are plain CRUD
   * against the customer's own aldelo.com-relying-party-scoped credentials
   * (server-side RPID-filtered, see customer_passkey_handler.go) and run no
   * new WebAuthn ceremony bound to the CURRENT page's origin, so they are
   * safe and useful to show from any mount. Registration is different: it
   * calls navigator.credentials.create() against THIS page's origin, which
   * the browser's own RP-ID check will reject outright on a mount that
   * isn't the aldelo.com relying party -- e.g. the /m/cart-review/{id}
   * standalone mount, which resolves to the OPERATOR's own domain
   * (storefront.aldelo.ai), not aldelo.com. Before this fix, SecurityPanel
   * was the only one of the three ceremony surfaces (see
   * ./passkey-ceremony-errors.ts's header) that had no eligibility gate at
   * all -- a customer following a cart-review link, signing in by OTP
   * (correctly gated, no passkey button there), and opening Account ->
   * Security saw a fully enabled Add-a-Passkey form whose every ceremony
   * failed permanently, with no explanation.
   */
  passkeyEligible?: boolean;
}

export function SecurityPanel({ passkeyEligible }: SecurityPanelProps) {
  const t = useEmbedTokens();

  // Remediation B1: isPasskeySupported() reads window.PublicKeyCredential at
  // call time (never a build-time constant -- same reasoning as
  // SignInPanel's showPasskeySignIn), so this re-evaluates per render rather
  // than freezing a stale platform read taken once on mount.
  const passkeySupported = isPasskeySupported();
  // Fix-round-2 I-1: BOTH gates must hold to offer registration -- see
  // SecurityPanelProps.passkeyEligible's doc comment above.
  const canAddPasskey = passkeyEligible === true && passkeySupported;

  const [passkeys, setPasskeys] = useState<PasskeySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listPasskeys();
      setPasskeys(data);
    } catch (err) {
      setLoadError(messageOf(err, "Failed to load your passkeys"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Add ----
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState(false);

  const handleAdd = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setAdding(true);
      setAddError(null);
      setAddSuccess(false);
      setRemovalAnnouncement(null); // TD-440: stale "X removed." shouldn't outlive an unrelated add
      try {
        const created = await registerPasskey(newName.trim());
        // Rule 59: the new row comes from the mutation response -- the
        // server assigned id/created_at, which a local guess couldn't have.
        setPasskeys((prev) => [...prev, created]);
        setNewName("");
        setAddSuccess(true);
      } catch (err) {
        if (isCancellation(err)) {
          // The customer dismissed the authenticator prompt -- an ordinary,
          // expected outcome (design ruling), never rendered as an error.
        } else {
          setAddError(ceremonyMessageOf(err, "Couldn't add this passkey. Please try again."));
        }
      } finally {
        setAdding(false);
      }
    },
    [newName],
  );

  // ---- Rename ----
  const [editingId, setEditingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);

  const startRename = useCallback((pk: PasskeySummary) => {
    setEditingId(pk.id);
    // Pre-fill with the raw value (empty for a null/unnamed row) -- never
    // the "Unnamed passkey" fallback label, which is display-only text, not
    // a value that should round-trip back into the field.
    const raw: unknown = pk.friendly_name;
    setRenameValue(typeof raw === "string" ? raw : "");
    setRenameError(null);
  }, []);

  const cancelRename = useCallback(() => {
    setEditingId(null);
    setRenameError(null);
  }, []);

  const handleRename = useCallback(
    async (id: string) => {
      setRenamingId(id);
      setRenameError(null);
      // Remediation (Group D): clear a stale "Passkey added." banner on ANY
      // subsequent mutation, not only when the customer types in the add
      // form's Nickname field -- a rename or delete performed right after an
      // add left the old success banner visible and unrelated to what the
      // customer just did.
      setAddSuccess(false);
      setRemovalAnnouncement(null); // TD-440: stale "X removed." shouldn't outlive an unrelated rename
      try {
        const updated = await renamePasskey(id, renameValue.trim());
        // Rule 59: re-seed THIS row from the response, not the locally-typed
        // value -- the server may normalize (trim/cap) differently.
        setPasskeys((prev) => prev.map((p) => (p.id === id ? updated : p)));
        setEditingId(null);
      } catch (err) {
        setRenameError(messageOf(err, "Couldn't rename this passkey. Please try again."));
      } finally {
        setRenamingId(null);
      }
    },
    [renameValue],
  );

  // ---- Delete ----
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Remediation B3, AS AMENDED BY W1 (review, 2026-08-31): clicking "Delete"
  // unmounts the very <button> that held focus (the EmbedLink below is
  // conditionally rendered away), dropping focus to document.body with no
  // announcement. B3's original fix moved focus straight to "Confirm
  // Delete" -- but a <button> activates on KEYDOWN, and OS key-repeat keeps
  // delivering keydown to whatever holds focus: a keyboard user who focuses
  // "Delete" and holds Enter fires keydown #1 (activates Delete), this
  // effect then moves focus to "Confirm delete" within the same frame, and
  // the FIRST auto-repeat (~500ms later) activates that -- deleting the
  // credential off a single held key, with the R1 disclosure on screen for
  // under a second and never read. (Space is unaffected; it activates on
  // keyup.) So focus goes to the DISCLOSURE instead, not the destructive
  // control: this keeps the announcement, keeps the two-step deliberate
  // (Confirm delete still needs its own separate activation the key-repeat
  // can't reach), and -- because a plain focused <div> with no aria-label
  // announces its own text CONTENT -- means the disclosure copy is what
  // gets read even if an AT suppresses the role="alert" live-region
  // announcement for landing focus inside that region in the same tick (a
  // known behavior on some screen readers). Only ONE row is ever pending
  // delete at a time (pendingDeleteId is panel-level, not per-row), so a
  // single ref -- attached only to the currently-pending row's disclosure
  // div -- is enough.
  const disclosureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pendingDeleteId) {
      disclosureRef.current?.focus();
    }
  }, [pendingDeleteId]);

  // TD-440: successful deletion unmounts the ENTIRE row -- including
  // "Confirm delete", whichever control held focus -- so the browser drops
  // focus to document.body with nothing said to assistive tech. Deleting the
  // LAST passkey happens to announce correctly anyway, because the
  // empty-state role="status" paragraph below is a NEW element entering the
  // page; deleting one of SEVERAL (the routine case) removes only that row
  // and nothing new enters the page, so nothing is announced at all -- the
  // one case with no feedback is the routine one. Fix: a status region that
  // is always at the SAME position in this card (above the list, so it
  // survives regardless of which row or how many remain) receives BOTH the
  // announcement text (naming the credential, same displayName() the row
  // itself used) AND explicit focus, same "focus what the AT can actually
  // read" mechanism this file already established for the delete disclosure
  // (disclosureRef, above) rather than trusting live-region-insertion alone.
  const [removalAnnouncement, setRemovalAnnouncement] = useState<string | null>(null);
  const removalStatusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (removalAnnouncement) {
      removalStatusRef.current?.focus();
    }
  }, [removalAnnouncement]);

  // TD-440 (fix round 2, review P2): the removal region is extracted so it
  // can render in BOTH branches below. Deleting the LAST passkey empties
  // the list, which flips `collapsed` true on the SAME render that sets
  // the announcement -- a collapsed early return alone would unmount the
  // announcement ("<label> removed." never renders) and strand focus on
  // document.body (the ref never attaches, so the .focus() effect above
  // no-ops). One JSX source for both branches; when nothing is pending
  // this is null and neither branch renders anything extra.
  const removalRegion = removalAnnouncement ? (
    <div
      ref={removalStatusRef}
      role="status"
      tabIndex={-1}
      style={{
        marginTop: t.spacing.md,
        display: "flex", alignItems: "center", gap: t.spacing.sm,
        background: t.colors.successBg, border: `1px solid ${t.colors.successGreen}`,
        color: t.colors.successGreen, borderRadius: t.borderRadius,
        padding: `${t.spacing.sm}px ${t.spacing.md}px`, fontSize: t.fontSize.sm,
        lineHeight: t.lineHeight.normal, boxSizing: "border-box",
      }}
    >
      {removalAnnouncement}
    </div>
  ) : null;

  const requestDelete = useCallback((id: string) => {
    setPendingDeleteId(id);
    setDeleteError(null);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDeleteId(null);
    setDeleteError(null);
  }, []);

  const confirmDelete = useCallback(
    async (id: string) => {
      // Remediation (Group D): "Confirm delete" stays live while the delete
      // is in flight -- ManageStoresPanel.tsx guards its equivalent confirm
      // action the same way. Belt-and-suspenders with the `disabled` prop on
      // the control below: this early-return protects the state transition
      // itself even if something other than a rendered click ever reaches
      // this callback while a delete for the SAME id is already in flight.
      if (deletingId === id) return;
      // Captured BEFORE the row leaves state -- displayName(pk) is not
      // recoverable once deletePasskey() succeeds and the row is filtered
      // out below.
      const target = passkeys.find((p) => p.id === id);
      const label = target ? displayName(target) : "Passkey";
      setDeletingId(id);
      setDeleteError(null);
      setAddSuccess(false); // see the same comment in handleRename above
      setRemovalAnnouncement(null); // clear any stale prior announcement
      try {
        await deletePasskey(id);
        setPasskeys((prev) => prev.filter((p) => p.id !== id));
        setPendingDeleteId(null);
        // TD-440: name what was removed -- this is the ONLY feedback an AT
        // user gets for the routine "one of several" case.
        setRemovalAnnouncement(`${label} removed.`);
      } catch (err) {
        setDeleteError(messageOf(err, "Couldn't remove this passkey. Please try again."));
      } finally {
        setDeletingId(null);
      }
    },
    [deletingId, passkeys],
  );

  // Task 8 (account-area redesign, 2026-10-04): the two PRE cards ("Passkeys"
  // + "Add a Passkey") collapse into ONE "Sign-in & passkeys" card. The dead
  // second card was the reason: an ineligible mount previously rendered the
  // Add-a-Passkey form or its dead explanation NO MATTER what the list held.
  // Now the add form (with no heading of its own -- the card's heading is the
  // only one) renders only when adding is genuinely available; when the list
  // has rows and adding is unavailable, the card shows just the list; and
  // when the list is empty AND adding is unavailable, the whole card gives
  // way to a single status line carrying the cause and the R2 reassurance
  // together. A load FAILURE still forces the card -- a collapsed help line
  // must never swallow the Retry affordance.
  const collapsed =
    !loading && loadError === null && passkeys.length === 0 && !canAddPasskey;

  if (collapsed) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
        {/* TD-440 (fix round 2, P2): a pending removal announcement renders
            here TOO -- deleting the LAST passkey is what FLIPS `collapsed`
            true, so this is the branch that renders on the announcement's
            own commit. With nothing pending it renders nothing and the
            ONE-status-element layout pin is untouched. */}
        {removalRegion}
        {/* ONE role="status" element (the layout pin counts them) carrying,
            in one text run: the empty-state fact, the HONEST cause (branched
            -- an unsupported browser must never be told the problem is this
            mount, and vice versa: the cause phrases stay distinct, "aren't
            supported in this browser" vs "isn't available here"), the R2
            reassurance, and the "extra option, not a replacement" framing
            the intro normally carries (the card -- and its intro -- do not
            render in this state). Kept as a SINGLE template string because
            Testing Library matches getByText against an element's DIRECT
            text nodes: the same sentence split across sibling elements is
            unmatchable, and the copy pins would fail on a wording that is
            semantically identical. */}
        <p role="status" style={{ margin: 0 }}>
          <EmbedText tone="secondary">
            {`You haven't added a passkey yet. ${
              passkeySupported
                ? "Adding a passkey isn't available here."
                : "Passkeys aren't supported in this browser, so adding one isn't possible right now."
            } This doesn't affect signing in by email -- that still works exactly as it does today. A passkey is an extra option, not a replacement.`}
          </EmbedText>
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      <EmbedCard>
        {/* Task 8: ONE card heading. The PRE pair ("Passkeys" / "Add a
            Passkey") is retired -- a heading naming the KIND of thing in a
            single-block card read as a form label, and the second one titled
            a permanently dead card on ineligible mounts. */}
        <EmbedHeading level={2}>Sign-in & passkeys</EmbedHeading>
        <div style={{ marginTop: t.spacing.sm }}>
          <EmbedText tone="secondary">
            Passkeys let you sign in with Touch ID, Face ID, Windows Hello, or a
            security key instead of a code. Signing in by email stays exactly
            as it is today -- a passkey is an extra option, not a replacement.
          </EmbedText>
        </div>

        {/* TD-440: lives at a position that never depends on which row (or
            how many) remain, so it is a stable focus target even though the
            row that triggered it has already unmounted. role="status"
            covers an AT that DOES pick up live-region insertion; the
            explicit .focus() (the effect above) covers one that doesn't --
            same alert+focus belt-and-suspenders pattern the delete
            disclosure below already uses. Styled inline to match
            EmbedBanner's success tone rather than nesting EmbedBanner
            itself: role/tabIndex/ref all need to land on the SAME element,
            and EmbedBanner doesn't forward a ref. (Fix round 2, P2: the
            element is the `removalRegion` const above -- the collapsed
            branch renders it too, because deleting the LAST passkey is
            what flips `collapsed` true.) */}
        {removalRegion}

        <div style={{ marginTop: t.spacing.md }}>
          {loadError && (
            <div style={{ marginBottom: t.spacing.md }}>
              <EmbedBanner tone="error">{loadError}</EmbedBanner>
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedButton type="button" variant="secondary" onClick={load}>
                  Retry
                </EmbedButton>
              </div>
            </div>
          )}

          {loading && (
            <p role="status" style={{ margin: 0 }}>
              <EmbedText tone="secondary">Loading your passkeys...</EmbedText>
            </p>
          )}

          {!loading && !loadError && passkeys.length === 0 && (
            <p role="status" style={{ margin: 0 }}>
              <EmbedText tone="secondary">You haven&apos;t added a passkey yet.</EmbedText>
            </p>
          )}

          {!loading && passkeys.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
              {passkeys.map((pk) => (
                <div key={pk.id} data-testid={`passkey-card-${pk.id}`}>
                  <EmbedCard padding={t.spacing.md}>
                    {editingId === pk.id ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
                        {renameError && <EmbedBanner tone="error">{renameError}</EmbedBanner>}
                        <EmbedField label="Nickname" htmlFor={`passkey-rename-${pk.id}`}>
                          <EmbedInput
                            id={`passkey-rename-${pk.id}`}
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            autoFocus
                          />
                        </EmbedField>
                        <div style={{ display: "flex", gap: t.spacing.sm }}>
                          <EmbedButton
                            type="button"
                            variant="primary"
                            size="sm"
                            disabled={renamingId === pk.id}
                            loading={renamingId === pk.id}
                            onClick={() => handleRename(pk.id)}
                          >
                            Save
                          </EmbedButton>
                          {/* TD-440: this row's own "Rename"/"Delete" links
                              already disambiguate by name (W2, above) --
                              this Cancel and the delete-disclosure's Cancel
                              below can be on screen SIMULTANEOUSLY (one row
                              mid-rename, a DIFFERENT row mid-delete-confirm),
                              both previously named bare "Cancel" with no way
                              to tell them apart by accessible name alone. */}
                          <EmbedButton
                            type="button"
                            variant="secondary"
                            size="sm"
                            aria-label={`Cancel renaming ${displayName(pk)}`}
                            onClick={cancelRename}
                          >
                            Cancel
                          </EmbedButton>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span style={{ fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text }}>
                          {displayName(pk)}
                        </span>
                        <div style={{ marginTop: t.spacing.xs }}>
                          <EmbedText tone="secondary">
                            {`Added ${formatRelativeOrAbsoluteDate(pk.created_at)} · Last used ${
                              pk.last_used_at ? formatRelativeOrAbsoluteDate(pk.last_used_at) : "never"
                            }`}
                          </EmbedText>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: t.spacing.md, marginTop: t.spacing.sm }}>
                          {/* W2 (review, 2026-08-31): with 2+ passkeys -- the
                              steady state friendly_name exists to support --
                              a bare "Rename"/"Delete" label collides across
                              rows for anyone working from the buttons list
                              (e.g. a screen reader's rotor), with nothing
                              indicating which credential each destroys.
                              aria-label reuses the SAME displayName() helper
                              the visible row label already uses -- no second
                              naming path. */}
                          <EmbedLink aria-label={`Rename ${displayName(pk)}`} onClick={() => startRename(pk)}>
                            Rename
                          </EmbedLink>
                          {pendingDeleteId !== pk.id && (
                            <EmbedLink
                              variant="danger"
                              aria-label={`Delete ${displayName(pk)}`}
                              onClick={() => requestDelete(pk.id)}
                            >
                              Delete
                            </EmbedLink>
                          )}
                        </div>

                        {pendingDeleteId === pk.id && (
                          // role="alert" makes assistive tech announce this
                          // block the instant it's inserted. W1 (review,
                          // 2026-08-31): disclosureRef (see its comment
                          // above) additionally moves FOCUS onto the
                          // disclosure div below, not onto "Confirm delete"
                          // -- so the disclosure text is what gets read even
                          // if an AT drops the live-region announcement for
                          // focus landing inside this region in the same
                          // tick, and a keyboard user's held Enter can't
                          // key-repeat straight through to the destructive
                          // control.
                          <div
                            role="alert"
                            style={{
                              marginTop: t.spacing.sm,
                              paddingTop: t.spacing.sm,
                              borderTop: `1px solid ${t.colors.cardBorder}`,
                            }}
                          >
                            {deleteError && (
                              <div style={{ marginBottom: t.spacing.sm }}>
                                <EmbedBanner tone="error">{deleteError}</EmbedBanner>
                              </div>
                            )}
                            {/* Operator ruling R1 (non-negotiable): this
                                deletes OUR SERVER RECORD ONLY. Never state or
                                imply removal from the customer's device. */}
                            <div
                              ref={disclosureRef}
                              id={`passkey-delete-disclosure-${pk.id}`}
                              tabIndex={-1}
                            >
                              <EmbedText tone="secondary">
                                This passkey will no longer sign you in here.
                                Your device may still list it -- remove it in
                                your device&apos;s own settings too, if you
                                want it gone there as well.
                              </EmbedText>
                            </div>
                            <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.sm }}>
                              <EmbedLink
                                variant="danger"
                                aria-describedby={`passkey-delete-disclosure-${pk.id}`}
                                disabled={deletingId === pk.id}
                                onClick={() => confirmDelete(pk.id)}
                              >
                                {deletingId === pk.id ? "Removing..." : "Confirm delete"}
                              </EmbedLink>
                              <EmbedLink
                                aria-label={`Cancel deleting ${displayName(pk)}`}
                                onClick={cancelDelete}
                              >
                                Cancel
                              </EmbedLink>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </EmbedCard>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Task 8: the add form moved INSIDE the "Sign-in & passkeys" card
            (its retired "Add a Passkey" heading was the second card's title),
            and it renders ONLY when adding is genuinely available -- the PRE
            dead second card showed an unusable form or explanation on
            ineligible mounts no matter what the list held. The mount-
            eligibility and browser-support gates themselves are UNCHANGED
            (canAddPasskey = strict === true && isPasskeySupported()); only
            the "explain why not" copy moved -- it is the collapsed state's
            single status line above, branched on the SAME two gates with the
            SAME distinct cause phrases. */}
        {canAddPasskey && (
          <form onSubmit={handleAdd} noValidate>
            {addError && (
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedBanner tone="error">{addError}</EmbedBanner>
              </div>
            )}
            {addSuccess && !addError && (
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedBanner tone="success">Passkey added.</EmbedBanner>
              </div>
            )}

            <div style={{ marginTop: t.spacing.md }}>
              <EmbedField
                label="Nickname"
                htmlFor="passkey-new-name"
                help="Optional -- e.g. 'iPhone' or 'Work laptop', to tell your passkeys apart."
              >
                <EmbedInput
                  id="passkey-new-name"
                  type="text"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setAddSuccess(false);
                  }}
                  placeholder="e.g. iPhone"
                />
              </EmbedField>
            </div>

            <div style={{ marginTop: t.spacing.md }}>
              <EmbedButton type="submit" variant="primary" disabled={adding} loading={adding}>
                {adding ? "Adding..." : "Add passkey"}
              </EmbedButton>
            </div>
          </form>
        )}
      </EmbedCard>
    </div>
  );
}
